import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// /verify-email?token=… — public page; confirms the address and refreshes the session if the user is signed in.
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState(params.get("token") ? "working" : "missing");
  useEffect(() => {
    const token = params.get("token");
    if (!token) return;
    api.post("/auth/verify-email", { token }).then(async () => { setState("done"); if (user) await refreshUser(); }).catch(() => setState("failed"));
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const ICON = { working: Loader2, done: CheckCircle2, failed: XCircle, missing: XCircle };
  const TONE = { working: "w-6 h-6 animate-spin text-cyan-300", done: "w-6 h-6 text-emerald-400", failed: "w-6 h-6 text-rose-400", missing: "w-6 h-6 text-rose-400" };
  const body = {
    working: [null, "Confirming your email…", ""],
    done: [null, "Email confirmed", "Paid plans, exchange connections and push alerts are now unlocked for your account."],
    failed: [null, "This link is invalid or has expired", "Links work once and expire after 24 hours. Sign in and request a new one from Settings."],
    missing: [null, "Confirmation link missing", "Open the link from your email, or sign in and request a new one from Settings."],
  }[state];
  const Icon = ICON[state];
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md apx-card p-6" data-testid={`verify-email-${state}`}>
        <div className="flex items-center gap-3"><Icon className={TONE[state]} /><h1 className="font-display text-2xl font-bold">{body[1]}</h1></div>
        <p className="text-sm text-slate-400 mt-3">{body[2]}</p>
        <div className="mt-6 text-sm"><Link to={user ? "/dashboard" : "/login"} className="text-cyan-300 hover:underline" data-testid="verify-email-continue">{user ? "Go to dashboard" : "Sign in"}</Link></div>
      </div>
    </div>
  );
}

// Banner for signed-in users whose address is not confirmed yet (accounts from before verification existed never see it).
export const VerifyEmailBanner = () => {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified !== false) return null;
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success("Confirmation email sent — check your inbox"); await refreshUser(); }
    catch (err) { toast.error(apiError(err, "Could not resend right now")); }
    finally { setBusy(false); }
  };
  return (
    <div className="apx-card p-3 border border-amber-800/60 bg-amber-500/5 flex flex-wrap items-center gap-3 text-sm" data-testid="verify-email-banner">
      <MailWarning className="w-4 h-4 text-amber-300 shrink-0" />
      <div className="flex-1 min-w-[200px] text-slate-200">Confirm your email address to unlock paid plans, exchange connections and push alerts. We sent a link to <span className="font-mono-ticker">{user.email}</span>.</div>
      <button type="button" onClick={resend} disabled={busy} className="text-xs rounded-full border border-amber-700 px-3 py-1 text-amber-200 hover:bg-amber-500/10 disabled:opacity-60" data-testid="verify-email-resend">{busy ? "Sending…" : "Resend link"}</button>
    </div>
  );
};
